import React from "react";

import { Status } from "@src/components/status";
import { OUTDATED, REQUEST_STATUS } from "@src/constants";
import SelectionFilter from "@src/features/table_filtering/components/selection_filter/selection_filter";
import { SwitchFilter } from "@src/features/table_filtering/components/switch_filter";
import { mapToTreeData } from "@src/features/table_filtering/utils/filter_utils";
import { setCustomTableFilter } from "@src/services/resourcePlanSliceV2";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import { TreeNode } from "@src/types/antd_types";
import { RESOURCE_PLAN_FILTER_TYPES_V2 } from "@src/types/resource_plan_types";

interface FilterResourcePlanProps {
  disabled: boolean;
  standardRoleNames: TreeNode[];
}

const FilterResourcePlan: React.FC<FilterResourcePlanProps> = ({
  disabled,
  standardRoleNames,
}) => {
  const dispatch = useAppDispatch();

  const {
    resourcePlanTableState: { customTableFilters },
  } = useAppSelector((state: RootState) => state.resourcePlanSliceV2);

  /**
   * updates the state of the custom table filter by the given type.
   *
   * @param {string[] | boolean} values - The values of the filter.
   * @param {RESOURCE_PLAN_FILTER_TYPES} type - The type of the filter.
   */
  function updateCustomTableFilterByType(
    values: string[] | boolean,
    type: RESOURCE_PLAN_FILTER_TYPES_V2
  ) {
    dispatch(
      setCustomTableFilter({
        ...customTableFilters,
        [type]: values,
      })
    );
  }

  return (
    <>
      <SwitchFilter
        title="Show only"
        switches={[
          {
            label: "Require Actions",
            checked: customTableFilters.actionRequired,
            onFilter: () => {
              updateCustomTableFilterByType(
                !customTableFilters.actionRequired,
                RESOURCE_PLAN_FILTER_TYPES_V2.ACTION_REQUIRED
              );
            },
          },
          {
            label: "Declined by project",
            checked: customTableFilters.isDeclined,
            onFilter: () => {
              updateCustomTableFilterByType(
                !customTableFilters.isDeclined,
                RESOURCE_PLAN_FILTER_TYPES_V2.DECLINED_BY_PROJECT
              );
            },
          },
          {
            label: "New Comments",
            checked: customTableFilters.isCommentAvailable,
            onFilter: () => {
              updateCustomTableFilterByType(
                !customTableFilters.isCommentAvailable,
                RESOURCE_PLAN_FILTER_TYPES_V2.NEW_COMMENTS
              );
            },
          },
          {
            label: "Joint Venture",
            checked: customTableFilters.jointVenture,
            onFilter: () => {
              updateCustomTableFilterByType(
                !customTableFilters.jointVenture,
                RESOURCE_PLAN_FILTER_TYPES_V2.JOINT_VENTURE
              );
            },
          },
        ]}
      />
      <SelectionFilter
        title="Role"
        treeData={standardRoleNames}
        disabled={disabled}
        defaultKeys={customTableFilters.standardRoleName}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            RESOURCE_PLAN_FILTER_TYPES_V2.STANDARD_ROLE_NAMES
          );
        }}
      />
      <SelectionFilter
        title="Status"
        treeData={mapToTreeData([
          REQUEST_STATUS.REQUESTED,
          REQUEST_STATUS.IN_PROGRESS,
          REQUEST_STATUS.PROPOSED,
          REQUEST_STATUS.IN_RECRUITMENT,
          REQUEST_STATUS.DECLINED,
          REQUEST_STATUS.CONFIRMED,
          OUTDATED,
        ])}
        disabled={disabled}
        defaultKeys={customTableFilters.status}
        customRenderer={(node: TreeNode) => (
          <Status status={node.title.toString()} />
        )}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            RESOURCE_PLAN_FILTER_TYPES_V2.STATUS
          );
        }}
      />
    </>
  );
};

export default FilterResourcePlan;
